/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import {
    getPerformances,
    fetchProjectTokens,
    fetchUserInvestmentsByProjectID,
    getOrders,
    getProjectOffers,
    checkEthereumBalance,
    fetchProductCatalogProjects,
    getProduct,
} from "api";
import { useFormattedMessage, useUtils } from "hooks";
import { fetchUserDetailsStart } from "store/user/account/actions";
import { fetchProjectStart, fetchMarketplaceProjectSuccess, setSelectedProjectId } from "store/project/actions";
import { selectProjectDetailsForId } from "store/project/selectors";
import { selectContinueInvestments, selectFullInvestmentDetails } from "store/user/investment/selectors";
import { resetInvestmentStepData } from "store/user/investment/actions";
import { selectPlatformSourceFromApp, selectTenant, selectLocale } from "store/app/selectors";
import {
    selectUserDetails,
    selectUserRoles,
    selectIsLoggedIn,
    selectEmail,
    selectStatusMangopay,
    selectStatusSecondaryMarket,
    selectInterestedInProjects,
    selectIsUserFullyOnBoarded,
    selectIsUserStatusEmailVerified,
    selectUserTermsAndConditionsAccepted,
    selectIsUserOnboardingCompleted,
    selectAvailableAssetForSelectedProject,
} from "store/user/account/selectors";

import {
    USER_ROLES,
    BUY_ORDER_TYPE,
    CAPITAL_INVESTED_STATUS,
    typeInvestmentProductValues,
    getInfoBlockData,
    WEB_FLOW_ROUTES,
    hasCombinedRetailClubDealTenant,
    hasGfkOnboardingProduct,
    getProductCategoriesTranslatedLabels,
    PRODUCT_CATEGORIES,
} from "constants/index";
import {
    SECONDARY_MARKET_STATUS,
    PROJECT_STATUS,
    MANGOPAY_STATUS,
    CLUB_DEAL_STATUS,
    ORDER_STATUS,
    PROJECT_STATUS_ALLOWED_DETAIL_PAGE,
} from "constants/status";

import { addOverlay, sideModalScrollHidden, removeOverlay, getUserEmail } from "utils";
import { getTenantFromHost, getBaseApiURL } from "common/helper";
import ToolTip from "components/ToolTip";
import MetaTags from "components/MarketplaceDetail/MetaTags";
import ExpandedTabsBlock from "components/MarketplaceDetail/ExpandedTabsBlock";
import KPIBlock from "components/MarketplaceDetail/KPIBlock";
import UserOrders from "components/MarketplaceDetail/UserOrders";
import SharesSection from "components/MarketplaceDetail/SharesSection";
import InfoBox from "components/MarketplaceDetail/InfoBox";
import BackButton from "components/BackButton";
import BuySharesModal from "components/BuySharesModal";
import Disclaimer from "components/Disclaimer";
import Highlights from "components/Highlights";
import InfoBlock from "components/InfoBlock";
import InvestmentModal from "components/InvestmentModal";
import KycModal from "components/KycModal";
import PropertyInterestModal from "components/PropertyInterestModal";
import SellSharesModal from "components/SellSharesModal";
import PCDetailSlider from "components/PCDetailSlider";
import SecondaryMarketplaceOnboarding from "components/SecondaryMarketplaceOnboarding";
import BuySharesEditModal from "components/BuySharesEditModal";
import SellSharesEditModal from "components/SellSharesEditModal";
import SingletonApi from "api/SingletonApi";
import {
    ActionButtonsWithLogin,
    ActionButtonsWithoutLogin,
    ClubDealButtonsWithLogin,
    ClubDealButtonsWithoutLogin,
} from "components/MarketplaceButtons";
import Facebook from "assets/images/svg/fb.svg";
import Linkedin from "assets/images/svg/linkedin.svg";
import Link from "components/NavLink";

const FINX_COOKIE = require("common/constants");
const PerformancePlotlyChart = dynamic(import("components/PerformancePlotlyChart"), { ssr: false });

const messages = {
    predictedTotalReturn: "property_predicted_total_return",
    assetfooterNote: "property_asset_footer_note",
    realClubfooterNote: "property_real_club_footer_note",
    financingVolume: "financing_volume",
    InfoBlockHeading: "how_it_works",
    InfoBlockLinkText: "learn_more",
    ordersHeading: "orders_heading",
    forecastDistribution: "forecast_distribution",
    forecastRepayment: "forecast_repayment",
    forecastIncrease: "forecast_increase",
    propertyType: "property_type",
    indicativeAssessment: "indicative_assessment",
    currentPurchasePrice: "current_purchase_price",
    developmentSincePurchase: "development_since_purchase",
    readyToMove: "ready_to_move",
    allInformationInvestment: "all_information_investment",
    footerNote: "property_footer_note",
    investmentHighlightsHeading: "investment_highlights",
    yourDigitalShares: "property_your_digital_shares",
    amount: "amount",
    investmentAmount: "investment_amount",
    totalValue: "total_value",
    profit: "profit",
    Performance: "price_chart",
    shareShortText: "pc",
    indicativeWealthText: "property_indicative_wealth",
    indicativePerformanceText: "property_indicative_performance",
    from: "from",
    privateEquityFooterNote: "property_private_equity_footernote",
    shareOn: "share_on",
    shareOnTermsNote: "share_on_terms_note",
    shareOnTermsNoteEnd: "share_on_terms_note_end",
    andThe: "and_the",
    privacyPolicy: "privacy_policy",
    transparencyDocument: "transparency_document",
};

const unitValue = { k: 1000 };

const defaultInvestmentShare = 500;

const MarketplaceDetailPage = ({
    isLoggedIn,
    setSelectedProjectId,
    fetchProjectStart,
    projectData = {},
    userEmail,
    interestedInProject = [],
    userRoles = [],
    fetchUserDetailsStart,
    continueInvestmentData,
    isUserInvestmentsLoading,
    statusClubDeal,
    statusMangopay,
    statusSecondaryMarket,
    locale,
    isApp,
    resetInvestmentStepData,
    isUserFullyOnBoarded,
    isUserStatusEmailVerified,
    isUserTermsAndConditionsAccepted,
    isUserOnboardingCompleted,
    availableAsset,
    currentTenant,
}) => {
    const { formatMessage } = useFormattedMessage();
    const PRODUCT_CATEGORIES_TRANSLATED_LABELS = getProductCategoriesTranslatedLabels();
    const router = useRouter();
    const { URLDataPrivacy, URLTransparency, Domain } = useUtils().tenant;
    const isTenantFinexity = useUtils().isTenantFinexity;
    const [isProjectInterested, setProjectInterested] = useState(false);
    const [obj, setObj] = useState(null);
    const [activeKycStep, setActiveKycStep] = useState(false);
    const [kycAtStep1, setKycAtStep1] = useState(false);
    const [backOnInvestment, setBackOnInvestment] = useState(false);
    const [isTypeClub, setIsTypeClub] = useState(false);
    const [hideReserveSection, setHideReserveSection] = useState(false);
    const [userOrders, setUserOrders] = useState([]);
    const [investmentAmount, setInvestmentAmount] = useState(0);
    const [totalTokenRemaining, setTotalTokenRemaining] = useState(0);
    const [userProjectShares, setUserProjectShares] = useState(0);
    const [isUserProjectShares, setIsUserProjectShares] = useState(false);
    const [projectTokenData, setProjectTokenData] = useState();
    const [sharesIndicativeWealth, setSharesIndicativeWealth] = useState(0);
    const [amountDifference, setAmountDifference] = useState({ value: 0, percentage: 0 });

    const [projectOffers, setProjectOffers] = useState([]);
    const [clickedOrderUID, setClickedOrderUID] = useState(null);
    const [isMobileScreen, setMobileScreen] = useState(false);
    const [performances, setPerformances] = useState([]);
    const [isConResInvest, setIsConResInvest] = useState(false);

    const [isSingleProductLoading, setSingleProductLoading] = useState(false);
    const [isGfkOnboardingProduct, setGfkOnboardingProduct] = useState(false);
    const [isEwPgInvestmentProcess, setEwPgInvestmentProcess] = useState(false);

    //modals
    const [propertyInterestModalFlag, setPropertyInterestModalFlag] = useState(false);
    const [buySharesModalFlag, setBuySharesModalFlag] = useState(false);
    const [sellSharesModalFlag, setSellSharesModalFlag] = useState(false);
    const [kycModalFlag, setKycModalFlag] = useState(false);
    const [investmentModalFlag, setInvestmentModalFlag] = useState(false);
    const [secondaryMarketModalFlag, setSecondaryMarketModalFlag] = useState(false);
    const [buySharesEditModalFlag, setBuySharesEditModalFlag] = useState(false);
    const [sellSharesEditModalFlag, setSellSharesEditModalFlag] = useState(false);

    const [productData, setProductData] = useState({});

    const status = projectData?.status?.status;
    const projectID = router.query.id || projectData?.projectID;
    const totalReturnEstimate = projectData?.financials?.totalReturnEstimate;
    const perAnnoReturnEstimate = projectData?.financials?.perAnnoReturnEstimate;
    const perAnnoMortgageRepaymentEstimate = projectData?.financials?.perAnnoMortgageRepaymentEstimate;
    const perAnnoPropertyValueGrowthEstimate = projectData?.financials?.perAnnoPropertyValueGrowthEstimate;
    const objectType = projectData?.financials?.objectType;
    const finalizationDate = projectData?.financials?.finalizationDate;
    const numberOfShares = projectData?.financials?.numberOfShares;
    const typeProduct = productData?.TypeProduct;
    const categoryProduct = productData?.CategoryProduct;
    const title = projectData?.title;
    const address = projectData?.address;
    const combineAddress = address && `${address.street}, ${address.zipCode} ${address.city}`;
    const fundingPercentage = projectData?.fundingPercentage;
    const fundingNews = projectData?.financials?.fundingNews;
    const details = projectData?.details;
    const detailsTitle = details?.detailsTitle;
    const section = details?.sections;
    const documents = details?.documents;
    const highlights = projectData?.highlights;
    const images = projectData?.images;
    const slider = images?.slider;
    const teaserImage = images?.teaserImage;
    const teaserVideo = images?.teaserVideo;
    const VIPUser = userRoles?.length && !!userRoles.find((role) => role === USER_ROLES.VIP);
    const clubUser = userRoles?.length && !!userRoles.find((role) => role === USER_ROLES.CLUBDEALS);
    const mangopayUserActive = statusMangopay && statusMangopay === MANGOPAY_STATUS.ACTIVE;
    const secondaryMarketUserActive = statusSecondaryMarket && statusSecondaryMarket === SECONDARY_MARKET_STATUS.ACTIVE;
    const actionBox = projectData?.actionBox;
    const lines = actionBox?.line;
    const infoBox = projectData?.infoBox;
    const typeInvestmentProduct = projectData?.typeInvestmentProduct;

    const isUserCanReserve =
        status &&
        (status === PROJECT_STATUS.ADVERTISING ||
            (status === PROJECT_STATUS.VIP_FUNDING && !VIPUser) ||
            status === PROJECT_STATUS.CLUB_DEAL);
    const canUserInvest =
        status && (status === PROJECT_STATUS.FUNDING_ACTIVE || (status === PROJECT_STATUS.VIP_FUNDING && VIPUser));
    const isMarketplace = status && status === PROJECT_STATUS.MARKETPLACE;
    const isMarketplacePaused = status && status === PROJECT_STATUS.MARKETPLACE_PAUSED;
    const isSuccessfullyPaidBack = status && status === PROJECT_STATUS.SUCCESSFULLY_PAID_BACK;

    const isHighlightsVisible = !isSuccessfullyPaidBack;
    const isAllowSecondaryMarket = !!(secondaryMarketUserActive && mangopayUserActive);
    const isSharesSectionVisible = isLoggedIn && (isMarketplace || isMarketplacePaused) && isAllowSecondaryMarket;
    const canShowAppleButton = isSharesSectionVisible && !!availableAsset?.uid;

    const isGraphVisible = isMarketplace || isMarketplacePaused || isSuccessfullyPaidBack;
    const isTypeClubWithShare = !!typeProduct?.split("-")?.[2];
    const typeClubShareNumber = typeProduct?.split("-")?.[2]?.trim()?.match(/\d+/g);
    const typeClubShareUnit = typeProduct
        ?.split("-")?.[2]
        ?.trim()
        ?.match(/[a-zA-Z]+/g);
    const typeClubShare =
        typeClubShareNumber && typeClubShareUnit && parseInt(typeClubShareNumber) * unitValue[typeClubShareUnit];

    const minInvestmentShare =
        (typeInvestmentProduct && typeInvestmentProductValues[typeInvestmentProduct]?.minAmount) ||
        typeClubShare ||
        defaultInvestmentShare;

    const maxInvestmentAmount = typeInvestmentProduct
        ? (typeInvestmentProductValues[typeInvestmentProduct]?.maxAmount ?? null)
        : null;

    const dividableBy = typeInvestmentProduct ? (typeInvestmentProductValues[typeInvestmentProduct]?.dividableBy ?? null) : null;

    useEffect(() => {
        jQuery(window).scrollTop(0);
        if (isApp) $(".main-app-container").scrollTop(0);
        if (window.outerWidth < 768) {
            setMobileScreen(true);
        }
    }, []);

    useEffect(() => {
        fetchProjectStart({ count: 1, projectID: projectID });
        const intervalId = setInterval(() => fetchProjectStart({ count: 1, projectID: projectID }), 1800000); // 1800000 = 30 minute
        return (function (id) {
            return function () {
                clearInterval(id);
            };
        })(intervalId);
    }, []);

    useEffect(() => {
        isLoggedIn && fetchUserDetailsStart();
    }, [isLoggedIn]);

    useEffect(() => {
        router?.query?.goto && setIsConResInvest(router?.query?.goto === "openInvestment");
    }, [router?.query?.goto]);

    useEffect(() => {
        totalTokenRemaining &&
            projectTokenData?.indicativeTokenValue &&
            setSharesIndicativeWealth(parseFloat(projectTokenData.indicativeTokenValue) * totalTokenRemaining);
    }, [projectTokenData, totalTokenRemaining]);

    useEffect(() => {
        function diff(a, b) {
            return 100 * Math.abs((a - b) / ((a + b) / 2));
        }
        sharesIndicativeWealth &&
            investmentAmount &&
            setAmountDifference({
                value: sharesIndicativeWealth - investmentAmount,
                percentage: diff(sharesIndicativeWealth, investmentAmount),
            });
    }, [sharesIndicativeWealth, investmentAmount]);

    useEffect(() => {
        if (typeProduct?.includes("Club")) {
            setIsTypeClub(true);
        } else {
            setIsTypeClub(false);
        }

        projectData?.images && setObj([{ list: teaserVideo ? teaserVideo : teaserImage }].concat(slider));
    }, [projectData, productData]);

    useEffect(() => {
        let x = 0;
        const CI360 = setInterval(() => {
            if (window.CI360 && projectData) {
                window.CI360.init();

                const canvas = document.getElementsByTagName("canvas")[0];
                if (window.outerWidth < 500 && canvas) {
                    canvas.width = "300";
                    canvas.style.width = "300px";
                }
            }
            if (++x === 5) {
                clearInterval(CI360);
            }
        }, 2000);

        return () => {
            clearInterval(CI360);
            window.CI360.destroy();
        };
    }, [projectData]);

    useEffect(() => {
        projectID &&
            projectData?.status?.status === PROJECT_STATUS.MARKETPLACE &&
            isAllowSecondaryMarket &&
            fetchUserEthereumBalance(projectID);
        userEmail && projectID && isAllowSecondaryMarket && fetchUserOrders(projectID, userEmail);
        userEmail && projectID && fetchUserInvestments(projectID, userEmail);
    }, [userEmail, projectID, isAllowSecondaryMarket, projectData?.status?.status]);

    // Project performance
    useEffect(() => {
        isGraphVisible &&
            getPerformances({ projectID })
                .then((res) => {
                    if (res?.data.error && res?.data.errors[0]) {
                        console.log(res.data.errors[0]);
                    } else if (res?.data?.data?.performances?.performances) {
                        setPerformances(res.data.data.performances.performances || []);
                    }
                })
                .catch(() => {});
    }, [projectID, isGraphVisible]);

    // Temporary redirections
    useEffect(() => {
        if (!(projectData && isTypeClub)) {
            return;
        }
        if (!hasCombinedRetailClubDealTenant(currentTenant)) {
            if (isLoggedIn) {
                if (userEmail) {
                    if (clubUser) {
                        statusClubDeal !== CLUB_DEAL_STATUS.COMPLETED && router.push("/club-deals/onboarding");
                    } else {
                        window.location = "/program/club-deal#registration";
                    }
                }
            } else {
                router.push("/login", undefined, { shallow: true });
            }
        }
    }, [projectData, isTypeClub, userEmail, clubUser, isLoggedIn, statusClubDeal]);

    useEffect(() => {
        if (projectID) {
            setSelectedProjectId(projectID);

            fetchProductCatalogProjects(1, projectID)
                .then((response) => response?.data?.data?.productCatalogProjects?.projects)
                .then((projects) => {
                    const isDetailPageAllowed = PROJECT_STATUS_ALLOWED_DETAIL_PAGE[projects?.[0]?.status?.status];
                    if (!isDetailPageAllowed) router.push("/marketplace");
                });
        }

        return () => {
            setSelectedProjectId(null);
        };
    }, [projectID]);

    useEffect(() => {
        if (kycAtStep1) {
            setKycModalFlag(false);
            removeOverlay();
            setKycAtStep1(false);
        }
    }, [kycAtStep1]);

    useEffect(() => {
        // Fetch latest performance data
        isLoggedIn &&
            (isMarketplace || isMarketplacePaused) &&
            fetchProjectTokens({ projectID })
                .then((res) => {
                    if (res?.data?.data?.projectTokens?.projectTokens?.length && res.data.data.projectTokens.projectTokens[0]) {
                        setProjectTokenData(res.data.data.projectTokens.projectTokens[0]);
                    }
                })
                .catch(() => {});
    }, [isLoggedIn, isMarketplace, isMarketplacePaused]);

    let mobileDistance, desktopDistance;
    useEffect(() => {
        setTimeout(() => {
            const $window = jQuery(window),
                mobileTargetButton = jQuery(".mobile-display .buttons.invest-btn"),
                desktopTargetButton = jQuery(".desktop-display .buttons.invest-btn"),
                mobileDistance = mobileTargetButton.length && mobileTargetButton.offset().top;
            desktopDistance = desktopTargetButton.length && desktopTargetButton.offset().top - 60;

            window.outerWidth < 992 &&
                $window.scroll(function () {
                    mobileDistance !== 0 && $window.scrollTop() >= mobileDistance
                        ? jQuery(".animated-btn .action-btn").addClass("active")
                        : jQuery(".animated-btn .action-btn").removeClass("active");
                });
            window.outerWidth > 991 &&
                $window.scroll(function () {
                    desktopDistance !== 0 && $window.scrollTop() >= desktopDistance
                        ? jQuery(".property-details .details .prepositions .buying").addClass("active")
                        : jQuery(".property-details .details .prepositions .buying").removeClass("active");
                });
        }, 2000);
    }, [isLoggedIn, userEmail, projectData, mobileDistance, desktopDistance, slider?.length]);

    useEffect(() => {
        projectID && interestedInProject?.includes(projectID) && setProjectInterested(true);
    }, [interestedInProject, userEmail, isLoggedIn]);

    useEffect(() => {
        if (isLoggedIn && isConResInvest) {
            addOverlay();
            if (Object.keys(continueInvestmentData).length > 0) {
                !investmentModalFlag && setInvestmentModalFlag(true);
                sideModalScrollHidden(true);
            }
        }
    }, [continueInvestmentData, isConResInvest, isLoggedIn]);

    // Prevent external links for webview
    useEffect(() => {
        const linkTimeOut = setTimeout(() => {
            jQuery(".expanded-content a").click(function (e) {
                e.preventDefault();

                isApp
                    ? window?.ReactNativeWebView?.postMessage(
                          JSON.stringify({
                              external_url: jQuery(this).attr("href"),
                          }),
                      )
                    : window.open(jQuery(this).attr("href"), "_blank");
            });
        }, 1000);

        return () => clearTimeout(linkTimeOut);
    }, [userEmail, projectData]);

    useEffect(() => {
        projectID && projectData?.status?.status === PROJECT_STATUS.MARKETPLACE && fetchProjectOffers(projectID);
    }, [projectID, projectData?.status?.status]);

    useEffect(() => {
        resetInvestmentStepData({ step: "investmentSum" });

        projectID && getSingleProduct(projectID);
    }, [projectID]);

    const fetchUserOrders = async (projectID, email) => {
        try {
            const response = await getOrders({ projectID, email });
            if (response?.data?.data?.orders?.orders) {
                const activeOrders =
                    Array.isArray(response.data.data.orders.orders) &&
                    response.data.data.orders.orders.length > 0 &&
                    response.data.data.orders.orders.filter((x) => x.StatusOrder === ORDER_STATUS.ACTIVE);
                setUserOrders(activeOrders);
            }
        } catch (error) {
            /* error */
        }
    };

    const fetchUserInvestments = async (projectID, email) => {
        try {
            const response = await fetchUserInvestmentsByProjectID(projectID, email);
            if (
                response?.data?.data?.investments?.investments &&
                Array.isArray(response.data.data.investments.investments) &&
                response.data.data.investments.investments.length > 0
            ) {
                const investmentAmount = response.data.data.investments.investments
                    .filter((x) => CAPITAL_INVESTED_STATUS.includes(x.StatusInvestment))
                    .reduce((total, item) => parseFloat(item.TokenRemaining * item.SharePrice) + total, 0);

                const totalTokenRemaining =
                    response.data.data.investments.investments
                        .filter((x) => CAPITAL_INVESTED_STATUS.includes(x.StatusInvestment))
                        .reduce((total, obj) => parseFloat(obj.TokenRemaining) + total, 0) || 0;

                setInvestmentAmount(investmentAmount);
                setTotalTokenRemaining(totalTokenRemaining);
            }
        } catch (error) {
            /* error */
        }
    };

    const fetchProjectOffers = async (projectID) => {
        try {
            const response = await getProjectOffers(projectID);
            const projectOffers = response?.data?.data?.projectOffers?.projectOffers;
            if (projectOffers) {
                setProjectOffers(projectOffers);
            } else {
                setProjectOffers([]);
            }
        } catch (error) {
            /* error */
        }
    };

    const fetchUserEthereumBalance = async (projectID) => {
        try {
            setIsUserProjectShares(true);
            const response = await checkEthereumBalance(projectID);
            const amount = response?.data?.data?.checkEthereumBalance?.Amount || 0;

            setIsUserProjectShares(false);
            setUserProjectShares(amount);
        } catch (error) {
            /* error */
        }
    };

    const onInvestmentButtonClick = () => {
        !investmentModalFlag && setInvestmentModalFlag(true);
        sideModalScrollHidden(true);
        addOverlay();
    };

    const updateUserProjectShares = (orderShares, action = "create") => {
        if (action === "create") {
            setUserProjectShares(parseFloat(userProjectShares) - parseFloat(orderShares));
        } else if (action === "deactivate") {
            setUserProjectShares(parseFloat(userProjectShares) + parseFloat(orderShares));
        }
    };

    const propertyInfoBoxStatuses = [
        PROJECT_STATUS.ADVERTISING,
        PROJECT_STATUS.FUNDING_ACTIVE,
        PROJECT_STATUS.FINANCING_COMPLETED,
        PROJECT_STATUS.FUNDING_CLOSED,
        PROJECT_STATUS.VIP_FUNDING,
        PROJECT_STATUS.SUCCESSFULLY_PAID_BACK,
    ];

    const isPropertyInfoBoxValidStatus = propertyInfoBoxStatuses.includes(status);

    const orderItemClick = (type, data) => {
        setClickedOrderUID(data.uid);
        if (BUY_ORDER_TYPE.includes(type)) {
            setBuySharesEditModalFlag(true);
        } else {
            setSellSharesEditModalFlag(true);
        }
        addOverlay();
    };

    const deleteUserOrder = (orderID) => {
        let deletedUserOrders = [];
        deletedUserOrders = userOrders;
        const orderIndex = deletedUserOrders.findIndex((order) => order.uid === orderID);
        if (orderIndex > -1) {
            deletedUserOrders.splice(orderIndex, 1);
        }
        setUserOrders(deletedUserOrders);
        setBuySharesEditModalFlag(false);
        setSellSharesEditModalFlag(false);
        removeOverlay();
    };

    const updateUserOrder = (order) => {
        let updatedUserOrders = [...userOrders];
        const orderIndex = updatedUserOrders.findIndex((x) => x.uid === order.uid);
        const targetOrder = updatedUserOrders.find((x) => x.uid === order.uid);
        const updatedOrder = { ...targetOrder, ...order };
        updatedUserOrders[orderIndex] = orderIndex > -1 ? updatedOrder : order;
        if (orderIndex > -1) {
            updatedUserOrders[orderIndex] = updatedOrder;
        } else {
            updatedUserOrders = [order].concat(updatedUserOrders);
        }
        setUserOrders(updatedUserOrders);
    };

    const onBack = () => {
        const query = router?.query;
        delete query["id"];
        router.push(
            {
                pathname:
                    isLoggedIn && isTypeClub && !hasCombinedRetailClubDealTenant(currentTenant) ? "/club-deals" : "/marketplace",
                query: query,
            },
            undefined,
            { shallow: true },
        );
    };

    const getSingleProduct = (projectID) => {
        try {
            setSingleProductLoading(true);

            getProduct(projectID)
                .then((res) => {
                    res && setSingleProductLoading(false);
                    if (res.data.errors) {
                        return;
                    }
                    setProductData(res?.data?.data?.product?.product);
                    // Check if the product exist in the GFK products list
                    if (hasGfkOnboardingProduct(projectID)) {
                        setGfkOnboardingProduct(true);
                    }

                    // Check if the product is EWPG product
                    if (res?.data?.data?.product?.product?.RegulatoryFramework === "EWPG") {
                        setEwPgInvestmentProcess(true);
                    }
                })
                .catch(() => {
                    setSingleProductLoading(false);
                });
        } catch (error) {
            setSingleProductLoading(false);
        }
    };

    const actionButton = isLoggedIn ? (
        <ActionButtonsWithLogin
            status={status}
            VIPUser={VIPUser}
            projectOffers={projectOffers}
            isAllowSecondaryMarket={isAllowSecondaryMarket}
            setPropertyInterestModalFlag={setPropertyInterestModalFlag}
            isProjectInterested={isProjectInterested}
            isSingleProductLoading={isSingleProductLoading}
            isUserInvestmentsLoading={isUserInvestmentsLoading}
            onInvestmentButtonClick={onInvestmentButtonClick}
            setBuySharesModalFlag={setBuySharesModalFlag}
            setSecondaryMarketModalFlag={setSecondaryMarketModalFlag}
            setSellSharesModalFlag={setSellSharesModalFlag}
            isUserFullyOnBoarded={isUserFullyOnBoarded}
            isUserStatusEmailVerified={isUserStatusEmailVerified}
            isUserTermsAndConditionsAccepted={isUserTermsAndConditionsAccepted}
            isUserOnboardingNotCompleted={!isUserOnboardingCompleted}
        />
    ) : (
        <ActionButtonsWithoutLogin
            VIPUser={VIPUser}
            projectOffers={projectOffers}
            setSecondaryMarketModalFlag={setSecondaryMarketModalFlag}
        />
    );

    const clubDealButtons =
        isLoggedIn && (statusClubDeal === "true" || clubUser) ? (
            <ClubDealButtonsWithLogin
                status={status}
                isTypeClubWithShare={isTypeClubWithShare}
                projectOffers={projectOffers}
                onInvestmentButtonClick={onInvestmentButtonClick}
                setBuySharesModalFlag={setBuySharesModalFlag}
                setHideReserveSection={setHideReserveSection}
                setPropertyInterestModalFlag={setPropertyInterestModalFlag}
                isAllowSecondaryMarket={isAllowSecondaryMarket}
                setSellSharesModalFlag={setSellSharesModalFlag}
                setSecondaryMarketModalFlag={setSecondaryMarketModalFlag}
                isUserFullyOnBoarded={isUserFullyOnBoarded}
                isUserStatusEmailVerified={isUserStatusEmailVerified}
                isUserTermsAndConditionsAccepted={isUserTermsAndConditionsAccepted}
                isUserOnboardingNotCompleted={!isUserOnboardingCompleted}
            />
        ) : (
            <ClubDealButtonsWithoutLogin
                status={status}
                projectOffers={projectOffers}
                setSecondaryMarketModalFlag={setSecondaryMarketModalFlag}
            />
        );

    const informationBlock =
        isTenantFinexity && categoryProduct !== PRODUCT_CATEGORIES.TRANSPORTATION ? (
            <section className="right-info-block information-block">
                <InfoBlock
                    title={formatMessage(messages.InfoBlockHeading)}
                    paragraphs={[formatMessage(getInfoBlockData(categoryProduct)?.text)]}
                    linkText={formatMessage(messages.InfoBlockLinkText)}
                    linkHref={getInfoBlockData(categoryProduct)?.link}
                    color="rgb(241,245,245)"
                    isWebFlowLink={getInfoBlockData(categoryProduct)?.isWebFlowLink}
                />
            </section>
        ) : null;

    const kpiBlock = (
        <KPIBlock
            formatMessage={formatMessage}
            isUserInvestmentsLoading={isUserInvestmentsLoading}
            isSingleProductLoading={isSingleProductLoading}
            messages={messages}
            actionBox={actionBox}
            lines={lines}
            isTypeClub={isTypeClub}
            isTypeClubButton={clubDealButtons}
            categoryProduct={categoryProduct}
            typeProduct={typeProduct}
            isLoggedIn={isLoggedIn}
            userEmail={userEmail}
            isMobileScreen={isMobileScreen}
            actionButton={actionButton}
            totalReturnEstimate={totalReturnEstimate}
            perAnnoReturnEstimate={perAnnoReturnEstimate}
            perAnnoMortgageRepaymentEstimate={perAnnoMortgageRepaymentEstimate}
            perAnnoPropertyValueGrowthEstimate={perAnnoPropertyValueGrowthEstimate}
            objectType={objectType}
            finalizationDate={finalizationDate}
            isMarketplace={isMarketplace}
            numberOfShares={numberOfShares}
            currentTenant={currentTenant}
        />
    );

    return (
        <>
            <MetaTags projectID={projectID} locale={locale} formatMessage={formatMessage} currentTenant={currentTenant} />
            {isMarketplace ? (
                <SecondaryMarketplaceOnboarding
                    isPropertyDetail
                    open={secondaryMarketModalFlag}
                    onCloseButtonClick={() => {
                        jQuery(".secondary-market-side-modal").hasClass("opened") && removeOverlay();
                        setSecondaryMarketModalFlag(false);
                    }}
                />
            ) : null}
            {isAllowSecondaryMarket && (
                <>
                    <BuySharesEditModal
                        minShareTypeWise={minInvestmentShare}
                        maxInvestAmount={maxInvestmentAmount}
                        open={buySharesEditModalFlag}
                        onCloseButtonClick={() => {
                            jQuery(".buy-shares-edit-modal").hasClass("opened") && removeOverlay();
                            setBuySharesEditModalFlag(false);
                        }}
                        projectTokenData={projectTokenData}
                        orderData={userOrders?.find((x) => x.uid === clickedOrderUID)}
                        onUpdate={(order) => updateUserOrder(order)}
                        onDelete={(orderID) => deleteUserOrder(orderID)}
                    />

                    <SellSharesEditModal
                        minShareTypeWise={minInvestmentShare}
                        maxInvestmentAmount={maxInvestmentAmount}
                        updateUserProjectShares={(orderShares) => updateUserProjectShares(orderShares)}
                        open={sellSharesEditModalFlag}
                        onCloseButtonClick={() => {
                            jQuery(".sell-shares-edit-modal").hasClass("opened") && removeOverlay();
                            setSellSharesEditModalFlag(false);
                        }}
                        userProjectShares={userProjectShares || 0}
                        projectTokenData={projectTokenData}
                        orderData={userOrders?.find((x) => x.uid === clickedOrderUID)}
                        onUpdate={(order) => updateUserOrder(order)}
                        onDelete={(orderID) => deleteUserOrder(orderID)}
                    />
                </>
            )}
            {isLoggedIn && (
                <>
                    {canUserInvest ? (
                        <>
                            <InvestmentModal
                                productType={typeProduct}
                                productCatalog={categoryProduct}
                                isEwPgInvestmentProcess={isEwPgInvestmentProcess}
                                isGfkOnboardingProduct={isGfkOnboardingProduct}
                                minInvestShare={minInvestmentShare}
                                maxInvestAmount={maxInvestmentAmount}
                                dividableBy={dividableBy}
                                openInvestment={isConResInvest}
                                numberOfShares={numberOfShares}
                                open={investmentModalFlag}
                                backOnInvestment={backOnInvestment}
                                setBackOnInvestment={() => {
                                    setBackOnInvestment(false);
                                }}
                                hideInvestmentModal={() => {
                                    setInvestmentModalFlag(false);
                                    sideModalScrollHidden(false);
                                }}
                                onCloseButtonClick={() => {
                                    jQuery(".investment-modal").hasClass("opened") && removeOverlay();
                                    setInvestmentModalFlag(false);
                                    sideModalScrollHidden(false);
                                    setIsConResInvest(false);
                                }}
                                setInvestmentKycModalFlag={() => setKycModalFlag(true)}
                            />
                            <KycModal
                                open={kycModalFlag}
                                activeStep={activeKycStep}
                                setActiveStep={() => setActiveKycStep(false)}
                                setKycAtStep1={() => setKycAtStep1(true)}
                                onBackButtonClick={() => {
                                    jQuery(".kyc-modal").hasClass("opened") && removeOverlay();
                                    setKycModalFlag(false);
                                }}
                                onCloseButtonClick={() => {
                                    jQuery(".kyc-modal").hasClass("opened") && removeOverlay();
                                    setKycModalFlag(false);
                                }}
                            />
                        </>
                    ) : null}
                    {isMarketplace ? (
                        <>
                            <BuySharesModal
                                minShareTypeWise={minInvestmentShare}
                                maxInvestAmount={maxInvestmentAmount}
                                project={projectData}
                                projectTotalShares={numberOfShares}
                                projectOffers={projectOffers}
                                open={buySharesModalFlag}
                                projectTokenData={projectTokenData}
                                onInit={(order) => updateUserOrder(order)}
                                onCreate={(order) => updateUserOrder(order)}
                                onCloseButtonClick={() => {
                                    jQuery(".buy-shares-modal").hasClass("opened") && removeOverlay();
                                    setBuySharesModalFlag(false);
                                }}
                            />
                            <SellSharesModal
                                minShareTypeWise={minInvestmentShare}
                                maxInvestmentAmount={maxInvestmentAmount}
                                updateUserProjectShares={(orderShares) => updateUserProjectShares(orderShares)}
                                project={projectData}
                                projectOffers={projectOffers}
                                isUserProjectShares={isUserProjectShares}
                                userProjectShares={userProjectShares || 0}
                                open={sellSharesModalFlag}
                                projectTokenData={projectTokenData}
                                onInit={(order) => updateUserOrder(order)}
                                onCreate={(order) => updateUserOrder(order)}
                                onCloseButtonClick={() => {
                                    jQuery(".sell-shares-modal").hasClass("opened") && removeOverlay();
                                    setSellSharesModalFlag(false);
                                }}
                            />
                        </>
                    ) : null}
                    {isUserCanReserve || canUserInvest ? (
                        <PropertyInterestModal
                            minShareTypeWise={minInvestmentShare}
                            hideReserveSection={hideReserveSection}
                            open={propertyInterestModalFlag}
                            projectID={projectID}
                            title={title}
                            numberOfShares={numberOfShares}
                            onCloseButtonClick={() => {
                                jQuery(".property-interest-modal").hasClass("opened") && removeOverlay();
                                setHideReserveSection(false);
                                setPropertyInterestModalFlag(false);
                            }}
                        />
                    ) : null}
                </>
            )}
            <div className="small-main property-details broad-container">
                <>
                    <div className="back-button-container">
                        <div className="small-container flex justify-between align-center">
                            <BackButton onClick={onBack} />
                            <div className="animated-btn">
                                {isTypeClub && !hasCombinedRetailClubDealTenant(currentTenant)
                                    ? clubDealButtons
                                    : !isLoggedIn && !isMarketplace
                                      ? actionButton
                                      : !isUserInvestmentsLoading &&
                                        !isSingleProductLoading &&
                                        userEmail &&
                                        !isMarketplace &&
                                        actionButton}
                            </div>
                        </div>
                    </div>
                    {slider?.length && obj ? <PCDetailSlider obj={obj} className={isApp ? "pt-17" : ""} /> : null}

                    <div className={`small-container ${isApp && !(slider?.length && obj) ? "pt-20" : ""}`}>
                        <div className="flex justify-between align-center actions category-type">
                            <div className="d-flex">
                                {categoryProduct && (
                                    <span className="tag">{PRODUCT_CATEGORIES_TRANSLATED_LABELS[categoryProduct]}</span>
                                )}
                            </div>
                        </div>
                        <div className="flex column align-start details">
                            <section className="left-info-block">
                                <div className="flex justify-between">
                                    <h3 className="my-0">{title}</h3>
                                </div>
                                <span className="paragraph text-14 fw-500 d-block">{combineAddress}</span>
                                <div className="mobile-display">
                                    {kpiBlock}
                                    <span className="text-below-block">
                                        {actionBox?.legalDisclaimer || formatMessage(messages.footerNote)}
                                    </span>
                                </div>
                                <InfoBox
                                    isTypeClub={isTypeClub}
                                    isTypeClubWithShare={isTypeClubWithShare}
                                    status={status}
                                    projectData={projectData}
                                    isLoggedIn={isLoggedIn}
                                    VIPUser={VIPUser}
                                    fundingPercentage={fundingPercentage}
                                    investedAmount={numberOfShares && numberOfShares * 1}
                                    fundingNews={fundingNews}
                                    infoBox={infoBox}
                                    isMarketplace={isMarketplace}
                                    isMarketplacePaused={isMarketplacePaused}
                                    isPropertyInfoBoxValidStatus={isPropertyInfoBoxValidStatus}
                                    isSharesSectionVisible={isSharesSectionVisible}
                                />
                                {isSharesSectionVisible ? (
                                    <SharesSection
                                        formatMessage={formatMessage}
                                        messages={messages}
                                        totalTokenRemaining={totalTokenRemaining}
                                        investmentAmount={investmentAmount}
                                        sharesIndicativeWealth={sharesIndicativeWealth}
                                        amountDifference={amountDifference}
                                        containerClassName={isApp && canShowAppleButton ? "mb-10" : "mb-20"}
                                    />
                                ) : null}
                                {userOrders?.length > 0 && (
                                    <UserOrders
                                        formatMessage={formatMessage}
                                        messages={messages}
                                        userOrders={userOrders}
                                        orderItemClick={orderItemClick}
                                    />
                                )}
                                {isGraphVisible && (
                                    <div className="performance mt-20">
                                        <h3 className="mb-8">{formatMessage(messages.Performance)}</h3>
                                        <PerformancePlotlyChart
                                            className="mt-0"
                                            locale={locale}
                                            performances={performances}
                                            projectTitle={title}
                                        />
                                    </div>
                                )}
                                <div className="mobile-display">
                                    {isHighlightsVisible && highlights?.length ? (
                                        <section className="right-info-block invest-highlights">
                                            <div className="info-block">
                                                <h3 className="my-0">{formatMessage(messages.investmentHighlightsHeading)}</h3>
                                                <Highlights highlightsData={highlights} />
                                            </div>
                                        </section>
                                    ) : null}
                                    {informationBlock}
                                </div>
                                {section?.length || documents?.length ? (
                                    <ExpandedTabsBlock
                                        isLoggedIn={isLoggedIn}
                                        documents={documents}
                                        title={detailsTitle || formatMessage(messages.allInformationInvestment)}
                                        sections={section}
                                        news={projectData.news}
                                    />
                                ) : null}
                            </section>
                            <div className="desktop-display">
                                {kpiBlock}
                                {isHighlightsVisible && (
                                    <section className="right-info-block invest-highlights">
                                        <span className={`text-below-block pt-3 ${!highlights?.length ? "mb-0" : ""}`}>
                                            {actionBox?.legalDisclaimer || formatMessage(messages.footerNote)}
                                        </span>
                                        {highlights?.length ? (
                                            <div className="info-block">
                                                <h3 className="my-0">{formatMessage(messages.investmentHighlightsHeading)}</h3>
                                                <Highlights highlightsData={highlights} />
                                            </div>
                                        ) : null}
                                    </section>
                                )}
                                {informationBlock}
                                <div className="share-product">
                                    <h5 className="mt-0 mb-4">{formatMessage(messages.shareOn)}</h5>
                                    <div className="mb-6">
                                        <a
                                            href={`https://www.facebook.com/sharer/sharer.php?u=${Domain}/marketplace/${projectID}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-right mr-3"
                                        >
                                            <Facebook style={{ width: 30, height: 30 }} />
                                        </a>
                                        <a
                                            href={`https://www.linkedin.com/sharing/share-offsite/?url=${Domain}/marketplace/${projectID}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-right"
                                        >
                                            <Linkedin style={{ width: 30, height: 30 }} />
                                        </a>
                                    </div>
                                    <p className="text-14 fw-500 light-grey-color">
                                        {formatMessage(messages.shareOnTermsNote)}{" "}
                                        <Link
                                            isExternal
                                            className="primary-color d-inline-block"
                                            href={isTenantFinexity ? WEB_FLOW_ROUTES.PRIVACY_POLICY : URLDataPrivacy}
                                        >
                                            {formatMessage(messages.privacyPolicy)}
                                        </Link>{" "}
                                        {formatMessage(messages.andThe)}{" "}
                                        <Link isExternal className="primary-color d-inline-block" href={URLTransparency}>
                                            {formatMessage(messages.transparencyDocument)}
                                        </Link>
                                        {formatMessage(messages.shareOnTermsNoteEnd)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    {projectID && projectData?.disclaimerHtml && <Disclaimer text={projectData.disclaimerHtml} />}
                </>
            </div>
            <ToolTip />
        </>
    );
};

MarketplaceDetailPage.getInitialProps = async ({ store, isServer, req, res, query }) => {
    const customWindow = { location: { host: isServer ? req?.hostname : window.location.host } };
    const baseApiUrl = getBaseApiURL(customWindow);
    const currentTenant = getTenantFromHost(isServer ? req?.hostname : window.location.host);
    const userEmail = isServer ? req.cookies[FINX_COOKIE.USER_EMAIL] : getUserEmail();

    const projectId = query?.id;
    store.dispatch(setSelectedProjectId(projectId));

    let projects = [];

    SingletonApi.setBaseURL(baseApiUrl);
    SingletonApi.setHeader("finexity-tenant", currentTenant);

    const response = await fetchProductCatalogProjects(1, projectId, userEmail);
    projects = response?.data?.data?.productCatalogProjects?.projects;
    const isDetailPageAllowed = PROJECT_STATUS_ALLOWED_DETAIL_PAGE[projects?.[0]?.status?.status];

    if (isDetailPageAllowed) store.dispatch(fetchMarketplaceProjectSuccess(projects));
    else if (isServer) {
        res.writeHead(307, { Location: "/marketplace" }); // 307 is for temporary redirects.
        res.end();
    }

    return { isServer };
};

const mapStateToProps = (state) => ({
    projectData: selectProjectDetailsForId(state),
    continueInvestmentData: selectContinueInvestments(state),
    isUserInvestmentsLoading: selectFullInvestmentDetails(state).isUserInvestmentsLoading,
    isLoggedIn: selectIsLoggedIn(state),
    userEmail: selectEmail(state),
    statusClubDeal: selectUserDetails(state) && selectUserDetails(state).StatusClubDeal,
    userRoles: selectUserRoles(state),
    interestedInProject: selectInterestedInProjects(state),
    statusMangopay: selectStatusMangopay(state),
    statusSecondaryMarket: selectStatusSecondaryMarket(state),
    locale: selectLocale(state),
    userDetails: selectUserDetails(state),
    isApp: selectPlatformSourceFromApp(state),
    isUserFullyOnBoarded: selectIsUserFullyOnBoarded(state),
    isUserStatusEmailVerified: selectIsUserStatusEmailVerified(state),
    isUserTermsAndConditionsAccepted: selectUserTermsAndConditionsAccepted(state),
    isUserOnboardingCompleted: selectIsUserOnboardingCompleted(state),
    availableAsset: selectAvailableAssetForSelectedProject(state),
    currentTenant: selectTenant(state),
});

const mapDispatchToProps = (dispatch) => ({
    fetchProjectStart: (data) => dispatch(fetchProjectStart(data)),
    setSelectedProjectId: (id) => dispatch(setSelectedProjectId(id)),
    fetchUserDetailsStart: () => dispatch(fetchUserDetailsStart()),
    resetInvestmentStepData: (data) => dispatch(resetInvestmentStepData(data)),
    fetchMarketplaceProjectSuccess: (projects) => dispatch(fetchMarketplaceProjectSuccess(projects)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MarketplaceDetailPage);
